import { Input, Selector } from '@casumo/dojo-react';
import { ChevronDown, Close, Search } from '@casumo/dojo-react-icons';
import { useEffect, useMemo, useState } from 'react';
import useCommonTranslations from '../api/hooks/useCommonTranslations.tsx';
import Drawer from '../design-system/drawer.tsx';
import { useRegistrationStore } from '../store/store.ts';
import { TSelectorDrawerField } from '../types/types';
import { TEST_ID } from '../utils/constants.ts';
import { selectDrawerData } from '../utils/selectorDrawerData.ts';

const SelectorDrawer = ({ name, dialogTitle, label, validation, onBlur }: Partial<TSelectorDrawerField> & {
  name: string;
  onChange?: (e: string) => void;
  onBlur?: (field: Partial<TSelectorDrawerField>) => void;
}) => {
  const { options, metadataInitialValue } = selectDrawerData[name as keyof typeof selectDrawerData];
  const { formData, setFormData, metadata, setMetadata, setErrors, errors } = useRegistrationStore();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleIsOpen = (open: boolean) => setIsOpen(open);

  const {
    commonTranslations,
    isLoading,
  } = useCommonTranslations();

  const filteredOptions = useMemo(() =>
    options.filter(option => option.name.toLowerCase().includes(searchValue.toLowerCase())),
  [options, searchValue],
  );

  const initialValue = useMemo(
    () => formData?.[name] || options.find(option => option.code === metadata?.[metadataInitialValue])?.name || '',
    [metadata, metadataInitialValue, options]);

  useEffect(() => {
    if (metadata?.[metadataInitialValue]) {
      setFormData({
        [name]: formData?.[name] || options
          .find(option =>
            option.code === metadata[metadataInitialValue])?.name || '',
      });
    }
  }, []);

  const placeholder = isLoading ? '' : commonTranslations?.search ?? 'Search';

  return (
    <>
      <Input
        id={`${name}SelectorInput`}
        name={name}
        label={label}
        value={formData?.[name] || initialValue}
        onKeyDown={(e) => e.key === 'Enter' && handleIsOpen(true)}
        readOnly
        sufix={<span className="text-[24px]"><ChevronDown /></span>}
        onClick={() => handleIsOpen(true)}
        onBlur={() => onBlur?.({ name, validation })}
        errorMessage={errors[name]}
        error={!!errors[name]}
        data-error={errors[name] ? true : undefined}
        data-testid={TEST_ID.COMBOBOX_TRIGGER + name}
      />
      <Drawer isOpen={isOpen} setIsOpen={setIsOpen}>
        <div className="flex flex-col h-full w-full p-md overflow-hidden">
          <div className="flex justify-between text-white pb-sm font-bold">
            <h1 className="text-h-lg">{dialogTitle}</h1>
            <span className="p-2xs cursor-pointer text-[24px]" onClick={() => handleIsOpen(false)}>
              <Close />
            </span>
          </div>
          <Input
            id={`${name}Search`}
            name="search"
            placeholder={placeholder}
            variant="dark"
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            value={searchValue}
            prefix={<span className="text-[24px]"><Search /></span>}
            onClear={() => setSearchValue('')}
            clearable
            autoFocus
            data-testid={TEST_ID.SEARCH + name}
          />
          <Selector name={name} direction="vertical" variant="dark"
            className="flex-1 w-full p-0.5 mt-md overflow-y-auto">
            {filteredOptions.map(option => (
              <Selector.Radio
                key={option.code}
                className="flex gap-3xs"
                value={option.code}
                onClick={() => {
                  setErrors({ [name]: '' });
                  setFormData({ [name]: option.name });
                  setMetadata({
                    [`selected${name.charAt(0).toUpperCase() + name.slice(1)}`]:         option.name,
                    [`selected${name.charAt(0).toUpperCase() + name.slice(1)}Code`]:     option.code,
                    [`manuallySelected${name.charAt(0).toUpperCase() + name.slice(1)}`]: true,
                  });
                  setIsOpen(false);
                }}
                data-testid={TEST_ID.PREFIX + name + '-' + option.name}
              >
                {option.name}
              </Selector.Radio>
            ))}
          </Selector>
        </div>
      </Drawer>
    </>
  );
};

export default SelectorDrawer;
